/* global Swiper */

// Misc. dependencies.
import Component from '../site/Component';
import getInteractiveChildren from '../utilities/getInteractiveChildren';

/**
 * Component for a slider powered by Swiper.
 */
export default class SwiperSlider extends Component {
  /**
   * Removes the tabIndex attribute from all elements within the active slide.
   *
   * @param {NodeList|Array} items A list of elements to be updated.
   */
  static tabIndexAllow(items) {
    Array.from(items).forEach((item) => item.removeAttribute('tabindex'));
  }

  /**
   * Adds a negative tabIndex attribute to all elements within a hidden slide.
   *
   * @param {NodeList|Array} items A list of elements to be updated.
   */
  static tabIndexDeny(items) {
    Array.from(items).forEach((item) => item.setAttribute('tabindex', '-1'));
  }

  /**
   * Starts the component (called when Component is instantiated).
   */
  start() {
    // Bind class methods.
    this.init = this.init.bind(this);
    this.handleInit = this.handleInit.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
    this.handlePaging = this.handlePaging.bind(this);

    /**
     * The slides wrapper element.
     *
     * @type {NodeList|null}
     */
    this.swiperEl = this.element.querySelector(this.elements.swiperEl);

    /**
     * The slides wrapper.
     *
     * @type {HTMLListElement}
     */
    this.slides = this.element.querySelector(this.elements.slides);

    /**
     * The raw number of slides.
     *
     * Loop mode adds slides, so we use the pre-init children count.
     * https://github.com/nolimits4web/swiper/issues/456#issuecomment-28682260
     */
    this.slideCount = this.slides.children.length;

    /**
     * The ARIA live region.
     *
     * @type {HTMLElement}
     */
    this.liveRegion = this.element.querySelector(this.elements.liveRegion);

    /**
     * The list that will hold the paging buttons.
     *
     * @type {HTMLUListElement}
     */
    this.pagingList = this.element.querySelector(this.elements.pagingList);

    /**
     * Whether or not the slide changed via pagination buttons.
     *
     * @type {Boolean}
     */
    this.paginated = false;

    /**
     * The paging buttons (without parent <li>).
     * @type {Array}
     */
    this.pagingButtons = [];

    if (null !== this.swiperEl) {
      this.init();
    }
  }

  /**
   * Initializes Swiper.
   */
  init() {
    this.swiper = new Swiper(
      this.swiperEl,
      {
        loop: true,
        slideActiveClass: 'wp-block-hachette-plugin-slide--active',
        a11y: {
          prevSlideMessage: 'Previous Slide',
          nextSlideMessage: 'Next Slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
        },
        navigation: {
          nextEl: '.hbg-slider__controls-button--next',
          prevEl: '.hbg-slider__controls-button--prev',
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        on: {
          afterInit: this.handleInit,
          slideChange: this.handleSlideChange,
          slideChangeTransitionEnd: (swiper) => {
            const { activeIndex, slides } = swiper;

            if (this.paginated) {
              // Focus the active slide if we navigated via pagination buttons.
              slides[activeIndex].focus();

              // Clear the flag.
              this.paginated = false;
            }
          },
        },
      }
    );
  }

  /**
   * Complete slider setup.
   *
   * @param {Swiper} swiper The Swiper instance.
   */
  handleInit(swiper) {
    const { slides, realIndex } = swiper;

    // Allow slides to be focused by paging buttons.
    slides.forEach((slide) => slide.setAttribute('tabindex', '-1'));

    if (1 < this.slideCount) {
      for (let i = 0; i < this.slideCount; i += 1) {
        const goTo = (i + 1);

        // Create paging dots.
        const button = document.createElement('button');
        button.className = 'hbg-slider__paging-button';
        button.setAttribute('aria-label', `Go to slide ${goTo}`);
        button.setAttribute('data-go-to', goTo);
        button.addEventListener('click', this.handlePaging);

        if (i === realIndex) {
          button.setAttribute('aria-current', 'true');
        }

        // Saved independently for use in `handleSlideChange`.
        this.pagingButtons.push(button);

        // Add paging buttons to the list.
        const li = document.createElement('li');
        li.appendChild(this.pagingButtons[i]);

        this.pagingList.appendChild(li);
      }
    }

    // Finish setup.
    this.handleSlideChange(swiper);
  }

  /**
   * Handles Swiper slideChange events; also applies to initialization.
   *
   * @param {Swiper} swiper The Swiper instance.
   */
  handleSlideChange(swiper) {
    const {
      activeIndex,
      realIndex, // Adjusted for loop mode.
      slides,
    } = swiper;

    // Remove hidden content from tabindex.
    // Using `slides` ensures it's applied to all slides, including those added by `loop:true`.
    slides.forEach((slide, idx) => {
      const interactiveChildren = getInteractiveChildren(slide);

      if (activeIndex === idx) {
        this.constructor.tabIndexAllow(interactiveChildren);
      } else {
        this.constructor.tabIndexDeny(interactiveChildren);
      }
    });

    // Update paging buttons.
    this.pagingButtons.forEach((button, idx) => {
      if (realIndex === idx) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    // Update ARIA live region.
    if (null != this.liveRegion) {
      // eslint-disable-next-line max-len
      this.liveRegion.textContent = `Item ${realIndex + 1} of ${this.slideCount}`;
    }
  }

  /**
   * Click handler for paging buttons.
   *
   * @param {Event} event The event object.
   */
  handlePaging(event) {
    const { target: { dataset } } = event;

    if (! Number.isNaN(dataset.goTo)) {
      this.swiper.slideTo(Number(dataset.goTo));

      // Flag that the slide changed via pagination.
      this.paginated = true;
    }
  }
}
